import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, BOARD_FONT, AVAILABLE_SIGNALS, formatDuration, getSignalColor, shouldFlashOrange, formatAddressShort, composeAddress } from "./utils";
import { AssignUnitSubmenu, ChangeStatusSubmenu, UpdateStatusSubmenu, AddUnitToCallSubmenu, MCIModal, DispositionsSubmenu, NotificationModal, CSLAssignSubmenu } from "@/pages/ContextSubmenus";
import { StatsBar } from "./StatsBar";

// Fire apparatus categories — used to partition the dispatch board into
// EMS and Fire panes. Mirrors the backend FIRE_UNIT_TYPES constant.
const FIRE_UNIT_TYPES = new Set(["ENGINE", "BRUSH FIRE UNIT", "BATTALION CHIEF"]);
const isFireUnit = (u) => FIRE_UNIT_TYPES.has(((u && u.unit_type) || "").toUpperCase());

export function DispatchBoard({ units, jobs, dispatchAgencies, agencies, locationCoords, callTypesDB, gpsPositions, fetchData, dispatcherId, onEditJob }) {
  const [, setTick] = useState(0);
  // Iter 96 — Reopen Closed Jobs modal state.
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [closedJobs, setClosedJobs] = useState([]);
  const [closedLoading, setClosedLoading] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);
  const [reopenMsg, setReopenMsg] = useState("");

  const fetchClosedJobs = useCallback(async () => {
    setClosedLoading(true);
    setReopenMsg("");
    try {
      const r = await axios.get(`${API}/jobs-closed-today`);
      setClosedJobs(r.data?.jobs || []);
    } catch {
      setClosedJobs([]);
    } finally {
      setClosedLoading(false);
    }
  }, []);
  useEffect(() => {
    if (reopenModalOpen) fetchClosedJobs();
  }, [reopenModalOpen, fetchClosedJobs]);

  const handleReopenJob = async (job) => {
    if (reopenBusy) return;
    if (!window.confirm(`Reopen CAD #${job.cad_number} and reassign to ${job.closed_unit_name}?`)) return;
    setReopenBusy(true);
    setReopenMsg("");
    try {
      const r = await axios.post(`${API}/jobs/${job.id}/reopen`, { dispatcher: dispatcherId || "dispatcher" });
      if (r.data?.error) {
        setReopenMsg(r.data.error);
      } else {
        setReopenMsg(`✓ Reopened CAD #${job.cad_number} → ${job.closed_unit_name}.`);
        if (fetchData) fetchData();
        await fetchClosedJobs();
      }
    } catch (err) {
      setReopenMsg(err?.response?.data?.error || "Reopen failed.");
    } finally {
      setReopenBusy(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const availableUnits = units.filter((u) => u.logged_in && AVAILABLE_SIGNALS.includes(u.signal || 97) && dispatchAgencies.includes(u.agency));
  const availableEMSUnits = availableUnits.filter((u) => !isFireUnit(u));
  const availableFireUnits = availableUnits.filter((u) => isFireUnit(u));

  // ─── FIRE + EMS ACTIVITY tapes (iter 76 / 77 / 78) ───
  // Side-by-side panel showing the last 5 events for each fleet.
  // Collapsed by default to preserve vertical real estate; one shared
  // header toggles both columns. Refreshed instantly via WebSocket on
  // any event that produces an activity row (unit_updated covers status
  // changes / dispositions / hospital transports / 10-13 / 10-89; the
  // dedicated fire_signal broadcast covers signal activations). A 5s
  // polling fallback runs in parallel so a missed/dropped WS message
  // can never leave the tapes stale.
  const [fireActivity, setFireActivity] = useState([]);
  const [emsActivity, setEmsActivity] = useState([]);
  const [fireActivityOpen, setFireActivityOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    let ws = null;
    let reconnectTimer = null;
    let debounceTimer = null;

    const load = async () => {
      try {
        const [f, e] = await Promise.all([
          axios.get(`${API}/fire-activity?limit=5`),
          axios.get(`${API}/ems-activity?limit=5`),
        ]);
        if (alive) {
          setFireActivity(Array.isArray(f.data) ? f.data : []);
          setEmsActivity(Array.isArray(e.data) ? e.data : []);
        }
      } catch { /* keep prior tape on transient errors */ }
    };

    // Coalesce rapid WS bursts (e.g. assign + status_change in one
    // dispatcher action) into a single API hit ~150ms after the last
    // event. Keeps the tapes responsive without amplifying load.
    const refreshSoon = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(load, 150);
    };

    const connect = () => {
      try {
        const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^https/, "wss").replace(/^http/, "ws") + "/api/ws";
        ws = new WebSocket(wsUrl);
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            // unit_updated — covers every event that writes a unit_history
            // row through the EMS path (status_change, hospital_transport,
            // disposition, auto_post/unpost, emergency_13/cleared) AND the
            // fire status path. Refresh both tapes; backend filters to
            // the right fleet.
            if (msg.type === "unit_updated" || msg.type === "fire_signal") {
              refreshSoon();
            }
          } catch { /* ignore malformed messages */ }
        };
        ws.onclose = () => {
          if (!alive) return;
          reconnectTimer = setTimeout(connect, 2000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch { /* ignore — polling fallback handles it */ }
    };

    load();
    connect();
    const t = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(t);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) try { ws.close(); } catch {}
    };
  }, []);
  const fmtRelTs = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toTimeString().slice(0, 8);
    } catch { return "—"; }
  };
  // EMS event → display code/color mapping. Status changes show the
  // signal number, dispositions show the disposition code, etc.
  const emsRowDisplay = (row) => {
    const ev = row.event || "";
    const details = row.details || "";
    if (ev === "status_change") return { code: details.startsWith("10-") ? details : `10-${row.signal ?? "?"}`, color: "#88CCEE" };
    if (ev === "hospital_transport") return { code: "10-82", color: "#FFB870" };
    if (ev === "disposition") {
      const m = details.match(/\b(8[13][A-Z]?|9[1-6])\b/);
      return { code: m ? m[1] : "DISP", color: "#A5D6FF" };
    }
    if (ev === "auto_post") return { code: "10-89↑", color: "#33FF99" };
    if (ev === "auto_unpost") return { code: "10-89↓", color: "#888" };
    if (ev === "auto_par_97") return { code: "10-97", color: "#88CCEE" };
    if (ev === "emergency_13") return { code: "10-13", color: "#FF4040" };
    if (ev === "emergency_cleared") return { code: "10-13✗", color: "#888" };
    return { code: ev.toUpperCase(), color: "#CCC" };
  };

  const activeJobs = jobs.filter((j) => {
    if (!j.assigned_unit_id) return true;
    const unit = units.find((u) => u.id === j.assigned_unit_id);
    return unit && unit.logged_in && !AVAILABLE_SIGNALS.includes(unit.signal || 97);
  });

  const agencyPrimaryBorough = {};
  (agencies || []).forEach((a) => {
    const coverage = a.borough_coverage || "";
    const bList = coverage.split(",").map((b) => b.trim()).filter(Boolean);
    agencyPrimaryBorough[a.name] = bList.length > 0 ? bList[0] : "OTHER";
  });

  const boroughGroups = {};
  // Citywide-EMS units are grouped by borough (existing behavior).
  // Citywide-Fire units are grouped only by agency (no borough display per spec).
  const citywideFireByAgency = {};
  // Iter 81 — operator spec: Fire units must be visible on the Citywide
  // Units tab at all times when active and available, INCLUDING own-agency
  // fire units. Previously own-agency fire was excluded (it lives in the
  // AVAILABLE UNITS pane); now it shows in BOTH so dispatchers can monitor
  // the entire city's fire activity at a glance.
  // Only show logged-in units on the citywide board
  units.filter((u) => u.logged_in).forEach((u) => {
    if (isFireUnit(u)) {
      if (!citywideFireByAgency[u.agency]) citywideFireByAgency[u.agency] = [];
      citywideFireByAgency[u.agency].push(u);
      return;
    }
    const borough = (u.csl_borough && u.signal === 89) ? u.csl_borough : (agencyPrimaryBorough[u.agency] || "OTHER");
    if (!boroughGroups[borough]) boroughGroups[borough] = {};
    if (!boroughGroups[borough][u.agency]) boroughGroups[borough][u.agency] = [];
    boroughGroups[borough][u.agency].push(u);
  });
  const boroughOrder = Object.keys(boroughGroups).sort();
  const citywideFireAgencies = Object.keys(citywideFireByAgency).sort();

  // ─── 10-13 Emergency banner ───
  // Build a list of every unit currently in 10-13 (logged_in filter keeps it
  // relevant — if logoff auto-clears, this is also naturally purged).
  const emergencyUnits = units.filter((u) => u.emergency_active).map((u) => ({
    id: u.id,
    name: u.name,
    borough: (u.csl_borough && u.signal === 89) ? u.csl_borough : (agencyPrimaryBorough[u.agency] || "OTHER"),
    activated_at: u.emergency_activated_at,
  }));
  // 1-second re-render tick so the "since ___" duration counter keeps ticking.
  const [, setEmgTick] = useState(0);
  useEffect(() => {
    if (emergencyUnits.length === 0) return;
    const t = setInterval(() => setEmgTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [emergencyUnits.length]);
  const emgSince = (iso) => {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.max(0, Math.floor(ms / 1000));
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}:${String(ss).padStart(2, "0")}`;
  };
  const clearEmergency = async (unitId) => {
    try { await axios.put(`${API}/units/${unitId}/emergency`, { active: false }); } catch {}
  };

  const renderUnitRow = (u, contextType) => {
    const isAvailable = AVAILABLE_SIGNALS.includes(u.signal || 97);
    // Look up the job this unit is on — as PRIMARY or as an ASSIST. Assist
    // units are attached via job.assist_units[], so they should render with
    // the same CAD #, call code, and location as the primary.
    const job = isAvailable ? null : jobs.find((j) => {
      if (j.status === "completed" || j.status === "cancelled") return false;
      if (j.assigned_unit_id === u.id) return true;
      return (j.assist_units || []).some((a) => (a?.unit_id || a) === u.id);
    });
    const ctMatch = job && callTypesDB ? callTypesDB.find((c) => c.call_type === job?.call_type) : null;
    const jobIsFire = job && (job.service_type || "EMS").toUpperCase() === "FIRE";
    const callCode = ctMatch ? ctMatch.call_code : (job ? job.call_type : "");
    const jobNumber = job ? (jobIsFire ? (job.fire_job_number || job.cad_number) : job.cad_number) : "";
    let locationDisplay = job ? formatAddressShort(composeAddress(job.location, job.apt)) : (isAvailable ? (u.cross_street_location || "") : "");
    if ((u.signal === 82 || u.signal === 81) && u.hospital_transport?.hospital_code && job) {
      locationDisplay = `${u.hospital_transport.hospital_code} \\ ${formatAddressShort(composeAddress(job.location, job.apt))}`;
    }
    // 10-13 emergency — aggressive red flash on every row that shows this unit.
    const isEmergency = !!u.emergency_active;
    const flashing = shouldFlashOrange(u);
    return (
      <div key={u.id} style={{ ...rowStyle, animation: isEmergency ? "flashRed 0.7s infinite" : flashing ? "flashOrange 1s infinite" : undefined }} onContextMenu={(e) => handleContextMenu(e, { ...u, job }, contextType)}>
        <span style={{ minWidth: 100, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
          {u.name}
          {/* NO CAD badge — visible when this unit was logged on from the
              VAC RCC form without an in-vehicle MDT. Tells dispatchers to
              radio for status instead of expecting MDT acknowledgments. */}
          {u.no_cad && (
            <span data-testid={`no-cad-badge-${u.name}`}
              style={{
                fontSize: 8, fontWeight: 900, letterSpacing: 0.5,
                background: "#CC2222", color: "#FFF",
                padding: "1px 4px", borderRadius: 2, lineHeight: 1,
                border: "1px solid #880000",
              }}>
              NO CAD
            </span>
          )}
          {/* SUPV tag — same format as NO CAD but blue. Marks supervisor
              units so dispatchers can identify command-staff at a glance. */}
          {(u.unit_type || "").toUpperCase() === "SUPERVISOR" && (
            <span data-testid={`supv-badge-${u.name}`}
              style={{
                fontSize: 8, fontWeight: 900, letterSpacing: 0.5,
                background: "#1E5DA8", color: "#FFF",
                padding: "1px 4px", borderRadius: 2, lineHeight: 1,
                border: "1px solid #0E3A6F",
              }}>
              SUPV
            </span>
          )}
        </span>
        <span style={{ minWidth: 50, color: getSignalColor(u.signal ?? 97), fontWeight: 700 }}>
          {(() => {
            // Iter 81 — Fire spec: when a fire unit is unassigned (no active
            // job), always display "10-08" — never "10-97" / "10-98" / "10-99".
            // Once assigned, show the real signal (10-63, 10-84, etc.) so the
            // dispatcher can read the apparatus's progression.
            if (isFireUnit(u) && !job) return "10-08";
            if (u.signal === 0) return "NOSTAT";
            if (u.signal === 10) return "DISP";
            return u.signal || 97;
          })()}
        </span>
        <span style={{ minWidth: 60, color: jobIsFire ? "#FF8844" : undefined, fontWeight: jobIsFire ? 700 : undefined }}>{jobNumber}</span>
        <span style={{ minWidth: 70, color: "#AAA" }}>{job ? callCode : ""}</span>
        <span style={{ flex: 1 }}>{locationDisplay}</span>
        <span style={{ minWidth: 50, textAlign: "right", color: "#AAA" }}>
          {formatDuration(u.status_changed_at)}
        </span>
      </div>
    );
  };

  const headerStyle = {
    background: "#00BFFF", color: "#FFF", textAlign: "center",
    padding: "6px 0", fontSize: 14, fontFamily: BOARD_FONT,
    fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
  };

  const boroughHeaderStyle = {
    background: "#005577", color: "#FFF", textAlign: "center",
    padding: "5px 0", fontSize: 14, fontFamily: BOARD_FONT,
    fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
  };

  const agencySubHeaderStyle = {
    background: "#003344", color: "#88CCEE", textAlign: "left",
    padding: "3px 10px", fontSize: 12, fontFamily: BOARD_FONT,
    fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #444",
  };

  const rowStyle = {
    display: "flex", padding: "4px 10px", fontFamily: BOARD_FONT,
    fontSize: 14, color: "#FFF", alignItems: "center", cursor: "default",
  };

  const [contextMenu, setContextMenu] = useState(null);
  const handleContextMenu = (e, item, type) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, item, type });
  };

  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  // Persist the right-clicked item even after the context menu closes so modal
  // submit handlers can still resolve the correct job / unit.
  const [modalContext, setModalContext] = useState(null);
  const closeMenu = () => { setContextMenu(null); setActiveSubmenu(null); };

  // Disposition handler at component level (not inside closure)
  const handleDisposition = async (dispoData) => {
    if (!contextMenu) return;
    const item = contextMenu.item;
    // For assignments, the unit comes from item.unit or item.assigned_unit_id.
    // item.id is the JOB id for assignments — do NOT use it as a fallback unit id.
    const isAssignment = contextMenu.type === "assignment";
    const unitId = item?.unit?.id || item?.assigned_unit_id || (!isAssignment ? item?.id : null);
    const jobId = item?.job?.id || (isAssignment ? item?.id : null);
    const dispoStr = dispoData?.disposition || "";
    const codeMatch = dispoStr.match(/10-(\S+)/);
    const code = codeMatch ? codeMatch[1] : dispoStr;
    const comment = dispoData?.comment || "";
    if (unitId) {
      // Unit-assigned call: disposition moves unit + completes job (handled server-side).
      try {
        await axios.post(`${API}/units/${unitId}/disposition`, { unit_id: unitId, code, comment });
        if (fetchData) fetchData();
      } catch {}
    } else if (jobId) {
      // PENDING call (no unit assigned): save disposition on the job and mark completed
      // so it is removed from the dispatch board (no unit history since it was never assigned).
      try {
        await axios.put(`${API}/jobs/${jobId}/status`, {
          status: "completed",
          disposition_code: code,
          comment,
        });
        if (fetchData) fetchData();
      } catch {}
    }
    closeMenu();
  };

  useEffect(() => {
    const handler = () => closeMenu();
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  const menuItems = [
    { id: "assign", label: "Assign Unit" },
    { id: "changestatus", label: "Change Status" },
    { id: "updatestatus", label: "Update Status" },
    { id: "addunit", label: "Add Unit to Call" },
    { id: "editcall", label: "Edit Call" },
    { id: "mci", label: "MCI" },
    { id: "dispositions", label: "Dispositions" },
    { id: "notification", label: "Notification" },
  ];

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: BOARD_FONT, background: "#000" }}>
      <StatsBar />
      {/* 10-13 persistent alert banner — one row per active emergency.
          Catches the dispatcher's eye no matter which section they're looking at. */}
      {emergencyUnits.length > 0 && (
        <div data-testid="emergency-banner" style={{ animation: "flashRed 0.7s infinite", borderBottom: "2px solid #660000" }}>
          {emergencyUnits.map((eu) => (
            <div key={eu.id} style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 18,
              padding: "6px 12px", color: "#FFF", fontFamily: BOARD_FONT, fontWeight: 900,
              fontSize: 15, letterSpacing: 1, textTransform: "uppercase",
            }}>
              <span style={{ fontSize: 18 }}>🚨</span>
              <span>10-13 ACTIVE:</span>
              <span style={{ fontWeight: 900 }}>{eu.name}</span>
              <span style={{ opacity: 0.9 }}>({eu.borough})</span>
              <span>SINCE {eu.activated_at ? new Date(eu.activated_at).toLocaleTimeString("en-GB") : "—"}</span>
              <span style={{ background: "#000", padding: "2px 10px", borderRadius: 2, fontFamily: "'IBM Plex Mono', monospace" }}>
                {emgSince(eu.activated_at)}
              </span>
              <button
                data-testid={`clear-emergency-${eu.name}`}
                onClick={() => clearEmergency(eu.id)}
                style={{ marginLeft: 8, background: "#FFF", color: "#CC0000", border: "none", padding: "3px 12px", fontWeight: 800, fontSize: 12, cursor: "pointer", borderRadius: 2 }}
              >
                CLEAR 10-13
              </button>
            </div>
          ))}
        </div>
      )}
      <div data-testid="dispatch-board-title" style={{ ...headerStyle, fontSize: 18, padding: "8px 0", letterSpacing: 2 }}>
        DISPATCH BOARD
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", padding: "6px", gap: 0 }}>
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", overflow: "hidden", border: "2px solid #444" }}>
          {/* AVAILABLE UNITS — split EMS vs Fire, stacked vertically per spec. */}
          <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", borderRight: "2px solid #444" }}>
            <div style={headerStyle} data-testid="board-available-header">AVAILABLE UNITS</div>
            <div style={{ flex: 1, overflow: "auto", background: "#000" }}>
              <div data-testid="board-available-ems-section">
                <div style={{ ...agencySubHeaderStyle, background: "#003B66", color: "#88CCEE" }}>EMS</div>
                <div data-testid="board-available-units">
                  {availableEMSUnits.map((u) => renderUnitRow(u, "available_unit"))}
                  {availableEMSUnits.length === 0 && (
                    <div style={{ padding: 10, color: "#666", fontFamily: BOARD_FONT, fontSize: 13 }}>No available EMS units</div>
                  )}
                </div>
              </div>
              <div data-testid="board-available-fire-section" style={{ borderTop: "2px solid #444" }}>
                <div style={{ ...agencySubHeaderStyle, background: "#5C2A00", color: "#FFB870" }}>FIRE</div>
                <div data-testid="board-available-fire-units">
                  {availableFireUnits.map((u) => renderUnitRow(u, "available_unit"))}
                  {availableFireUnits.length === 0 && (
                    <div style={{ padding: 10, color: "#666", fontFamily: BOARD_FONT, fontSize: 13 }}>No available Fire units</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CITYWIDE UNITS — EMS by borough (existing), Fire by agency (no borough). */}
          <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={headerStyle} data-testid="board-citywide-header">CITYWIDE UNITS</div>
            <div style={{ flex: 1, overflow: "auto", background: "#000" }} data-testid="board-citywide-units">
              <div data-testid="board-citywide-ems-section">
                <div style={{ ...agencySubHeaderStyle, background: "#003B66", color: "#88CCEE" }}>EMS</div>
                {boroughOrder.length === 0 && (
                  <div style={{ padding: 10, color: "#666", fontFamily: BOARD_FONT, fontSize: 13 }}>No other agency EMS units</div>
                )}
                {boroughOrder.map((borough, bIdx) => {
                  const agenciesInBorough = Object.keys(boroughGroups[borough]).sort();
                  const multipleAgencies = agenciesInBorough.length > 1;
                  return (
                    <div key={borough} style={{ borderTop: bIdx > 0 ? "2px solid #444" : "none" }}>
                      <div style={boroughHeaderStyle}>{borough}</div>
                      {agenciesInBorough.map((agencyName) => {
                        const agencyUnits = boroughGroups[borough][agencyName];
                        return (
                          <div key={agencyName}>
                            {multipleAgencies && <div style={agencySubHeaderStyle}>{agencyName}</div>}
                            {agencyUnits.map((u) => renderUnitRow(u, "citywide"))}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              <div data-testid="board-citywide-fire-section" style={{ borderTop: "2px solid #444" }}>
                <div style={{ ...agencySubHeaderStyle, background: "#5C2A00", color: "#FFB870" }}>FIRE</div>
                {citywideFireAgencies.length === 0 && (
                  <div style={{ padding: 10, color: "#666", fontFamily: BOARD_FONT, fontSize: 13 }}>No other agency Fire units</div>
                )}
                {citywideFireAgencies.map((agencyName) => (
                  <div key={agencyName}>
                    <div style={agencySubHeaderStyle}>{agencyName}</div>
                    {citywideFireByAgency[agencyName].map((u) => renderUnitRow(u, "citywide"))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ACTIVE ASSIGNMENTS — fixed to 5/8 the previous pane height (~163px).
            Previously used flex:"1 1 260px" but flex-grow:1 caused it to still
            split ~50/50 with the available-units section above regardless of
            the basis. flex:"0 0 163px" pins the pane at 5/8 the old size so
            the available-units sections finally get the extra vertical room. */}
        <div style={{ display: "flex", flexDirection: "column", flex: "0 0 163px", maxHeight: 163, overflow: "hidden", border: "2px solid #444", borderTop: "none" }}>
          <div style={{ ...headerStyle, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 8px" }} data-testid="board-active-header">
            <span>ACTIVE ASSIGNMENTS</span>
            <button
              data-testid="reopen-closed-jobs-btn"
              onClick={() => setReopenModalOpen(true)}
              style={{ background: "#0a4a8a", color: "#FFF", border: "1px solid #1a6ac0", padding: "3px 10px", borderRadius: 3, fontSize: 11, fontFamily: BOARD_FONT, fontWeight: 700, cursor: "pointer", letterSpacing: 0.5 }}
            >
              REOPEN CLOSED JOBS
            </button>
          </div>
          <div style={{ flex: 1, overflow: "auto", background: "#000" }} data-testid="board-active-assignments">
            {activeJobs.flatMap((j) => {
              // One row for the primary + one row per assist unit. Each row
              // behaves like the primary — same CAD, call code, location.
              const primary = units.find((u) => u.id === j.assigned_unit_id);
              const rows = [{ job: j, unit: primary, unitName: j.assigned_unit_name, isPending: !j.assigned_unit_id, role: "primary" }];
              (j.assist_units || []).forEach((a) => {
                const au = units.find((x) => x.id === (a?.unit_id || a));
                rows.push({ job: j, unit: au, unitName: a?.unit_name || au?.name || "—", isPending: false, role: "assist" });
              });
              return rows.map((r) => {
                const { job: rowJob, unit, unitName, isPending, role } = r;
                const ageMs = new Date() - new Date(rowJob.created_at);
                const overTwoMin = ageMs > 120000;
                const shouldFlash = isPending && overTwoMin;
                const unitFlash = !isPending && unit && shouldFlashOrange(unit);
                const dispFlash = !isPending && unit && unit.signal === 10 && unit.status_changed_at &&
                  (Date.now() - new Date(unit.status_changed_at).getTime()) > 60000;
                // 10-13 emergency always wins — red flash overrides all other animations.
                const emergencyFlash = !isPending && unit && unit.emergency_active;
                const ctMatch = callTypesDB ? callTypesDB.find((c) => c.call_type === rowJob.call_type) : null;
                const rowIsFire = (rowJob.service_type || "EMS").toUpperCase() === "FIRE";
                const callCode = ctMatch ? ctMatch.call_code : rowJob.call_type;
                const rowJobNumber = rowIsFire ? (rowJob.fire_job_number || rowJob.cad_number) : rowJob.cad_number;
                return (
                  <div key={`${rowJob.id}-${role}-${unit?.id || "pending"}`}
                    style={{ ...rowStyle, animation: emergencyFlash ? "flashRed 0.7s infinite" : shouldFlash ? "flashYellow 1s infinite" : dispFlash ? "flashOrange 1s infinite" : unitFlash ? "flashOrange 1s infinite" : undefined }}
                    onContextMenu={(e) => handleContextMenu(e, { ...rowJob, unit }, "assignment")}>
                    <span style={{ minWidth: 100, fontWeight: 700, color: isPending ? "#FFCC00" : "#FFF" }}>
                      {unitName || (isPending ? "PENDING" : "—")}
                    </span>
                    <span style={{ minWidth: 50, color: isPending ? "#FFCC00" : getSignalColor(unit?.signal), fontWeight: 700 }}>
                      {isPending ? "---" : unit ? (unit.signal === 0 ? "NOSTAT" : unit.signal === 10 ? "DISP" : unit.signal) : ""}
                    </span>
                    <span style={{ minWidth: 60, color: rowIsFire ? "#FF8844" : undefined, fontWeight: rowIsFire ? 700 : undefined }}>{rowJobNumber}</span>
                    <span style={{ minWidth: 70, color: "#AAA" }}>{callCode}</span>
                    <span style={{ flex: 1 }}>
                      {(unit?.signal === 82 || unit?.signal === 81) && unit?.hospital_transport?.hospital_code
                        ? `${unit.hospital_transport.hospital_code} \\ ${formatAddressShort(composeAddress(rowJob.location, rowJob.apt))}`
                        : formatAddressShort(composeAddress(rowJob.location, rowJob.apt))}
                    </span>
                    <span style={{ minWidth: 50, textAlign: "right", color: "#AAA" }}>
                      {isPending ? formatDuration(rowJob.created_at) : formatDuration(unit?.status_changed_at)}
                    </span>
                  </div>
                );
              });
            })}
            {activeJobs.length === 0 && (
              <div style={{ padding: 10, color: "#666", fontFamily: BOARD_FONT, fontSize: 13 }}>No active assignments</div>
            )}
          </div>
        </div>

        {/* ACTIVITY TAPES (iter 76/77) — collapsible panel with FIRE on
            the left and EMS on the right, side-by-side. Single header
            toggles open/close. Each side shows its own last 5 events. */}
        <div data-testid="board-activity-tapes" style={{ display: "flex", flexDirection: "column", flex: fireActivityOpen ? "0 0 160px" : "0 0 26px", maxHeight: fireActivityOpen ? 160 : 26, overflow: "hidden", border: "2px solid #444", borderTop: "none", transition: "flex-basis 0.18s ease, max-height 0.18s ease" }}>
          <div
            data-testid="board-fire-activity-header"
            onClick={() => setFireActivityOpen((v) => !v)}
            style={{ ...headerStyle, background: "#2A2A2A", color: "#FFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 10px", fontSize: 12, letterSpacing: 1.5, userSelect: "none" }}
          >
            <span>{fireActivityOpen ? "▼" : "▶"} ACTIVITY — FIRE ({fireActivity.length}) │ EMS ({emsActivity.length})</span>
            <span style={{ fontSize: 10, color: "#AAA" }}>last 5 each — newest top</span>
          </div>
          {fireActivityOpen && (
            <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", overflow: "hidden", background: "#000" }}>
              {/* FIRE column */}
              <div data-testid="board-fire-activity-rows" style={{ borderRight: "2px solid #444", overflow: "auto" }}>
                <div style={{ ...agencySubHeaderStyle, background: "#5C2A00", color: "#FFB870" }}>FIRE</div>
                {fireActivity.length === 0 && (
                  <div style={{ padding: 8, color: "#666", fontFamily: BOARD_FONT, fontSize: 12 }}>No fire activity yet</div>
                )}
                {fireActivity.map((row) => {
                  const isSignal = row.event === "fire_signal";
                  const code = isSignal
                    ? (row.details || "").replace(/^FIRE SIGNAL\s*/i, "").trim()
                    : (row.event || "").toUpperCase();
                  return (
                    <div
                      key={row.id}
                      data-testid={`board-fire-activity-row-${row.id}`}
                      style={{ display: "flex", gap: 8, padding: "3px 8px", color: "#FFB870", fontFamily: BOARD_FONT, fontSize: 11, borderBottom: "1px solid #3A1A00" }}
                    >
                      <span style={{ minWidth: 60, color: "#FFE0C0", fontWeight: 700 }}>{row.unit_name || "—"}</span>
                      <span style={{ minWidth: 80, color: isSignal ? "#FFCC00" : "#FF8844", fontWeight: 700 }}>{code || "—"}</span>
                      <span style={{ flex: 1, color: "#CCC", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.details || ""}</span>
                      <span style={{ minWidth: 60, color: "#888", textAlign: "right", fontFamily: "monospace" }}>{fmtRelTs(row.timestamp)}</span>
                    </div>
                  );
                })}
              </div>

              {/* EMS column */}
              <div data-testid="board-ems-activity-rows" style={{ overflow: "auto" }}>
                <div style={{ ...agencySubHeaderStyle, background: "#003B66", color: "#88CCEE" }}>EMS</div>
                {emsActivity.length === 0 && (
                  <div style={{ padding: 8, color: "#666", fontFamily: BOARD_FONT, fontSize: 12 }}>No EMS activity yet</div>
                )}
                {emsActivity.map((row) => {
                  const disp = emsRowDisplay(row);
                  return (
                    <div
                      key={row.id}
                      data-testid={`board-ems-activity-row-${row.id}`}
                      style={{ display: "flex", gap: 8, padding: "3px 8px", color: "#88CCEE", fontFamily: BOARD_FONT, fontSize: 11, borderBottom: "1px solid #002238" }}
                    >
                      <span style={{ minWidth: 60, color: "#CFE6FF", fontWeight: 700 }}>{row.unit_name || "—"}</span>
                      <span style={{ minWidth: 80, color: disp.color, fontWeight: 700 }}>{disp.code}</span>
                      <span style={{ flex: 1, color: "#CCC", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.details || ""}</span>
                      <span style={{ minWidth: 60, color: "#888", textAlign: "right", fontFamily: "monospace" }}>{fmtRelTs(row.timestamp)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right-click Context Menu */}
      {contextMenu && (() => {
        const menuH = 340;
        const menuW = 220;
        const adjustedY = contextMenu.y + menuH > window.innerHeight ? contextMenu.y - menuH : contextMenu.y;
        const adjustedX = contextMenu.x + menuW > window.innerWidth ? contextMenu.x - menuW : contextMenu.x;

        if (contextMenu.type === "available_unit") {
          const handleCSLAssign = async (cslName) => {
            const unitId = contextMenu.item?.id;
            if (unitId) {
              try { await axios.put(`${API}/units/${unitId}/assign-csl?csl_name=${encodeURIComponent(cslName)}`); } catch {}
            }
            closeMenu();
          };
          return (
            <div data-testid="csl-context-menu" style={{ position: "fixed", left: adjustedX, top: adjustedY, zIndex: 2000, fontFamily: "'IBM Plex Sans', sans-serif" }}
              onClick={(e) => e.stopPropagation()}>
              <CSLAssignSubmenu onSelect={handleCSLAssign} onClose={closeMenu} />
            </div>
          );
        }

        const handleMenuAction = (id) => {
          if (id === "mci" || id === "notification") {
            // Snapshot the right-clicked context BEFORE closing the menu,
            // so the modal's async onSubmit still has a valid unit/job to target.
            setModalContext({ type: contextMenu?.type, item: contextMenu?.item });
            setActiveModal(id);
            setContextMenu(null);
          } else if (id === "editcall") {
            // Edit Call — parent takes over, switches to CAD tab and prefills form.
            const item = contextMenu?.item;
            const job = item?.job || item;
            if (onEditJob && job) onEditJob(job);
            closeMenu();
          } else {
            setActiveSubmenu(activeSubmenu === id ? null : id);
          }
        };

        const handleAssignUnit = async (unitId) => {
          const item = contextMenu.item;
          const jobId = item?.job?.id || item?.id;
          if (jobId) {
            try { await axios.put(`${API}/jobs/${jobId}/assign`, { unit_id: unitId }); if (fetchData) fetchData(); } catch {}
          }
          closeMenu();
        };

        const handleChangeStatus = async (statusCode) => {
          const item = contextMenu.item;
          const unitId = item?.unit?.id || item?.assigned_unit_id || item?.id;

          if (typeof statusCode === "object" && statusCode.type === "hospital_transport") {
            if (unitId) {
              try {
                await axios.put(`${API}/units/${unitId}/hospital-transport`, {
                  category: statusCode.category,
                  hospital_id: statusCode.hospital_id,
                  hospital_code: statusCode.hospital_code,
                  hospital_name: statusCode.hospital_name,
                  override: statusCode.override,
                });
                if (fetchData) fetchData();
              } catch (err) {
                const msg = err?.response?.data?.message || err?.response?.data?.error || "Hospital transport failed.";
                alert(`Status change failed: ${msg}`);
              }
            }
            closeMenu();
            return;
          }

          // Fire-mode status: payload is { type:"fire_status", code, label, signal }
          // Routes through /api/fire-mdt/status so unit_history gets the
          // operator-spec FIRE-prefixed entry (RESP / 10-84 / 10-08).
          if (typeof statusCode === "object" && statusCode.type === "fire_status") {
            if (unitId) {
              try {
                await axios.post(`${API}/fire-mdt/status`, {
                  unit_id: unitId,
                  code: statusCode.code,
                });
                if (fetchData) fetchData();
              } catch (err) {
                const msg = err?.response?.data?.message || err?.response?.data?.error || "Fire status change failed.";
                alert(`Status change failed: ${msg}`);
              }
            }
            closeMenu();
            return;
          }

          const signalMap = { 63: "en_route", 84: "on_scene", 82: "transporting", 81: "at_hospital", 98: "returning", 97: "available", 99: "available_hq", 89: "at_post" };
          if (unitId && signalMap[statusCode]) {
            // Iter 92 — ALWAYS send a non-empty dispatcher_id so the backend
            // recognises this as a dispatcher override and skips the crew-
            // style signal-progression guard. Empty string was previously
            // being treated as a crew action, causing skip transitions (e.g.
            // 84 → 81) to be silently rejected with a 200 + error payload.
            const effectiveDispId = (dispatcherId || "").trim() || "dispatcher";
            try {
              const res = await axios.put(`${API}/units/${unitId}/status`, {
                status: signalMap[statusCode],
                dispatcher_id: effectiveDispId,
              });
              // Backend returns HTTP 200 with `{error: "..."}` on signal-
              // progression violations. Surface those to the dispatcher so
              // they don't silently fail (previous behaviour was "click
              // does nothing").
              if (res?.data?.error) {
                alert(`Status change rejected: ${res.data.message || res.data.error}`);
              } else if (fetchData) {
                fetchData();
              }
            } catch (err) {
              const msg = err?.response?.data?.message || err?.response?.data?.error || "Status change failed.";
              alert(`Status change failed: ${msg}`);
            }
          }
          closeMenu();
        };

        const handleUpdateStatus = async (payload) => {
          const item = contextMenu.item;
          // Iter 92 — for "assignment" context, item is `{ ...job, unit }`
          // where item.id is the JOB id. Previously `item?.id || item?.unit?.id`
          // resolved to the job id and the /units/{jobId}/acknowledge call
          // 404'd silently. Check unit-shaped paths first.
          const unitId = item?.unit?.id || item?.assigned_unit_id || (contextMenu.type === "assignment" ? null : item?.id);
          // Fire-mode payload: { type:"fire_update_status", status, comment }
          // Logs the extension category + comment to unit_history without
          // changing the unit's signal (operator handles transitions
          // explicitly via Change Status).
          if (typeof payload === "object" && payload && payload.type === "fire_update_status") {
            if (unitId) {
              try {
                await axios.post(`${API}/units/${unitId}/fire-update-status`, {
                  status: payload.status,
                  comment: payload.comment || "",
                });
              } catch {}
            }
            closeMenu();
            return;
          }
          if (unitId) {
            try { await axios.put(`${API}/units/${unitId}/acknowledge`); } catch {}
          }
          closeMenu();
        };
        const handleAddUnit = async (unitId) => {
          // Add this unit as an ASSIST on the right-clicked call (does NOT replace primary).
          const item = contextMenu.item;
          const jobId = item?.job?.id || item?.id;
          if (jobId && unitId) {
            try {
              await axios.put(`${API}/jobs/${jobId}/assign`, {
                unit_id: unitId,
                is_assist: true,
                dispatcher: dispatcherId || "",
              });
              if (fetchData) fetchData();
            } catch {}
          }
          closeMenu();
        };

        return (
        <div data-testid="context-menu" style={{ position: "fixed", left: adjustedX, top: Math.max(0, Math.min(adjustedY, window.innerHeight - 400)), zIndex: 2000, display: "flex", alignItems: "flex-start", gap: 4, fontFamily: "'IBM Plex Sans', sans-serif", maxHeight: "90vh" }}
          onClick={(e) => e.stopPropagation()}>
          <div style={{ background: "linear-gradient(180deg, #F0F4F8 0%, #D8E0EC 100%)", border: "2px solid #6688BB", borderRadius: 4, boxShadow: "0 6px 24px rgba(0,0,0,0.5)", minWidth: menuW, padding: "4px 0" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "2px 4px 4px", borderBottom: "1px solid #AAB8CC" }}>
              <div onClick={closeMenu} style={{ display: "flex", gap: 4 }}>
                <div style={{ width: 18, height: 18, background: "#3366AA", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 11, color: "#FFF", fontWeight: 700 }}>_</div>
                <div data-testid="context-menu-close" onClick={closeMenu} style={{ width: 18, height: 18, background: "#CC3333", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 11, color: "#FFF", fontWeight: 700 }}>X</div>
              </div>
            </div>
            {menuItems.map((item) => (
              <div key={item.id} data-testid={`ctx-${item.id}`}
                style={{ padding: "8px 16px", fontSize: 14, color: "#222", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #C8D0DC", background: activeSubmenu === item.id ? "#B8C8E0" : "transparent" }}
                onClick={(e) => { e.stopPropagation(); handleMenuAction(item.id); }}>
                <span style={{ fontWeight: 500 }}>{item.label}</span>
                <span style={{ color: "#666", fontSize: 12 }}>&#9654;</span>
              </div>
            ))}
          </div>

          {activeSubmenu === "assign" && <AssignUnitSubmenu units={units} gpsPositions={gpsPositions} job={contextMenu.item?.job || contextMenu.item} locationCoords={locationCoords} onSelect={handleAssignUnit} onClose={closeMenu} />}
          {activeSubmenu === "changestatus" && (() => {
            // Fire-aware Change Status — when the right-clicked unit is a Fire
            // apparatus, render the operator-spec RESP/10-84/10-08 set.
            // Iter 92 — for "assignment" context, the unit lives at
            // contextMenu.item.unit (the row is { ...job, unit }); for
            // "available_unit" context, the item IS the unit. Handle both.
            const it = contextMenu.item;
            const u = it?.unit_type ? it : (it?.unit?.unit_type ? it.unit : null);
            const isFire = u && isFireUnit(u);
            return <ChangeStatusSubmenu onSelect={handleChangeStatus} onClose={closeMenu} job={contextMenu.item?.job || contextMenu.item} fireMode={isFire} />;
          })()}
          {activeSubmenu === "updatestatus" && (() => {
            const it = contextMenu.item;
            const u = it?.unit_type ? it : (it?.unit?.unit_type ? it.unit : null);
            const isFire = u && isFireUnit(u);
            return <UpdateStatusSubmenu onSelect={handleUpdateStatus} onClose={closeMenu} fireMode={isFire} />;
          })()}
          {activeSubmenu === "addunit" && <AddUnitToCallSubmenu units={units} gpsPositions={gpsPositions} job={contextMenu.item?.job || contextMenu.item} locationCoords={locationCoords} onSelect={handleAddUnit} onClose={closeMenu} />}
        </div>
        );
      })()}

      {/* Dispositions rendered as fixed overlay to avoid clipping */}
      {contextMenu && activeSubmenu === "dispositions" && (() => {
        const u = contextMenu.item?.unit_type ? contextMenu.item : null;
        const isFire = (u && isFireUnit(u)) || (contextMenu.item?.unit && isFireUnit(contextMenu.item.unit));
        return (
          <div style={{ position: "fixed", left: Math.min(contextMenu.x + 230, window.innerWidth - 480), top: 10, zIndex: 2100, maxHeight: "calc(100vh - 20px)", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}>
            <DispositionsSubmenu onSelect={handleDisposition} onClose={closeMenu} fireMode={isFire} />
          </div>
        );
      })()}

      {activeModal === "mci" && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000 }}
          onClick={() => { setActiveModal(null); setModalContext(null); }}>
          <div onClick={(e) => e.stopPropagation()}>
            <MCIModal
              job={modalContext?.item?.job || modalContext?.item}
              onClose={() => { setActiveModal(null); setModalContext(null); }}
              onSubmit={async (data) => {
                const item = modalContext?.item || {};
                const jobId = item?.job?.id || (modalContext?.type !== "available_unit" ? item?.id : null);
                if (!jobId) return;
                try {
                  await axios.post(`${API}/jobs/${jobId}/mci`, {
                    address: data.address || "",
                    staging_location: data.stagingLocation || "",
                    comments: data.comments || "",
                    dispatcher: dispatcherId || "",
                  });
                  if (fetchData) fetchData();
                } catch (err) {
                  console.error("MCI submit failed", err);
                }
              }}
            />
          </div>
        </div>
      )}

      {activeModal === "notification" && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000 }}
          onClick={() => { setActiveModal(null); setModalContext(null); }}>
          <div onClick={(e) => e.stopPropagation()}>
            <NotificationModal
              onClose={() => { setActiveModal(null); setModalContext(null); }}
              onSubmit={async (data) => {
                // Resolve the unit currently assigned to the right-clicked call.
                // Use the snapshotted modalContext (contextMenu itself was cleared when the modal opened).
                const item = modalContext?.item || {};
                const unitId = item?.unit?.id || item?.assigned_unit_id || item?.job?.assigned_unit_id || (modalContext?.type === "available_unit" ? item?.id : null);
                if (!unitId) return;
                try {
                  await axios.post(`${API}/units/${unitId}/notification`, { unit_id: unitId, dispatcher: dispatcherId || "", ...data });
                  if (fetchData) fetchData();
                } catch (err) {
                  console.error("notification failed", err);
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Iter 96 — Reopen Closed Jobs modal */}
      {reopenModalOpen && (
        <div data-testid="reopen-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setReopenModalOpen(false); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 720, maxHeight: "80vh", background: "#0f1218", border: "2px solid #1a6ac0", borderRadius: 4, display: "flex", flexDirection: "column" }}>
            <div style={{ background: "linear-gradient(135deg, #0a3a6a, #062042)", color: "#FFF", padding: "10px 14px", fontFamily: BOARD_FONT, fontWeight: 700, letterSpacing: 0.5, fontSize: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>REOPEN CLOSED JOBS — Today's Tour</span>
              <button data-testid="reopen-modal-close" onClick={() => setReopenModalOpen(false)}
                style={{ background: "#993333", color: "#FFF", border: "1px solid #cc4444", padding: "3px 10px", borderRadius: 3, fontWeight: 700, cursor: "pointer" }}>X</button>
            </div>
            <div style={{ padding: 12, color: "#AAA", fontFamily: BOARD_FONT, fontSize: 11, borderBottom: "1px solid #333" }}>
              Only jobs closed today with disposition <b>87, 90, 91, 93, 94, 95, or 96</b>. Clicking a row reopens the job with the same CAD number and reassigns to the unit that closed it out.
            </div>
            {reopenMsg && (
              <div data-testid="reopen-modal-msg" style={{ padding: "6px 12px", color: reopenMsg.startsWith("✓") ? "#7CFFA9" : "#FF9C9C", background: reopenMsg.startsWith("✓") ? "#0c4a25" : "#5a1a1a", fontFamily: BOARD_FONT, fontSize: 12 }}>
                {reopenMsg}
              </div>
            )}
            <div style={{ flex: 1, overflow: "auto", padding: 8 }}>
              {closedLoading && <div style={{ padding: 20, textAlign: "center", color: "#888", fontFamily: BOARD_FONT }}>Loading…</div>}
              {!closedLoading && closedJobs.length === 0 && (
                <div data-testid="reopen-empty" style={{ padding: 20, textAlign: "center", color: "#888", fontFamily: BOARD_FONT }}>
                  No reopenable closed jobs today.
                </div>
              )}
              {!closedLoading && closedJobs.map((j) => (
                <div key={j.id} data-testid={`reopen-job-${j.cad_number}`}
                  onClick={() => handleReopenJob(j)}
                  style={{ padding: "8px 12px", marginBottom: 4, background: "#181c26", border: "1px solid #2a3a55", borderRadius: 3, cursor: reopenBusy ? "wait" : "pointer", opacity: reopenBusy ? 0.5 : 1, fontFamily: BOARD_FONT, fontSize: 12, color: "#DDD", display: "grid", gridTemplateColumns: "70px 60px 90px 1fr 110px", gap: 10, alignItems: "center" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#22273a"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "#181c26"}>
                  <span style={{ color: "#00BFFF", fontWeight: 700 }}>#{j.cad_number}</span>
                  <span style={{ color: "#FF8844", fontWeight: 700 }}>{j.closed_disposition_code}</span>
                  <span style={{ color: "#AAA" }}>{j.closed_unit_name || "—"}</span>
                  <span>{j.location}</span>
                  <span style={{ color: "#888", textAlign: "right", fontSize: 10 }}>
                    {j.closed_at ? new Date(j.closed_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: "America/New_York" }) : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
